import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Modal from '../../../components/ui/Modal';

const CreateTaskModal = ({ onClose, onCreate, preselectedDate }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'normal',
    status: 'todo',
    due_date: preselectedDate ? preselectedDate?.toISOString()?.split('T')?.[0] : ''
  });
  const [errors, setErrors] = useState({});
  const [creating, setCreating] = useState(false);

  const priorityOptions = [
    { value: 'low', label: 'Low Priority' },
    { value: 'normal', label: 'Normal Priority' },
    { value: 'high', label: 'High Priority' },
    { value: 'urgent', label: 'Urgent' }
  ];

  const statusOptions = [
    { value: 'todo', label: 'To Do' },
    { value: 'in_progress', label: 'In Progress' }
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    const newErrors = {};
    if (!formData?.title?.trim()) newErrors.title = 'Task title is required';
    
    if (Object.keys(newErrors)?.length > 0) {
      setErrors(newErrors);
      return;
    }

    setCreating(true);
    try {
      await onCreate(formData);
    } finally {
      setCreating(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors?.[field];
        return newErrors;
      });
    }
  };

  return (
    <Modal 
      isOpen={true} 
      onClose={onClose} 
      size="medium"
      title="Create New Task"
      description="Add a task to your productivity dashboard"
      icon={<Icon name="Plus" size={20} color="var(--color-primary)" />}
    >
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="space-y-4">
          <Input
            type="text"
            label="Task Title"
            description="Brief description of what needs to be done"
            placeholder="e.g., Review marketing proposal"
            value={formData?.title || ''}
            onChange={(e) => handleChange('title', e?.target?.value)}
            error={errors?.title}
            required
          />

          <div>
            <label className="block text-sm font-medium text-foreground mb-2">
              Description
              <span className="text-muted-foreground ml-2 font-normal">(Optional)</span>
            </label>
            <textarea
              placeholder="Add more details about this task..."
              value={formData?.description || ''}
              onChange={(e) => handleChange('description', e?.target?.value)}
              rows={3}
              className="w-full px-4 py-3 rounded-lg border border-input bg-background text-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent transition-all duration-250 resize-none"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Select
              label="Priority"
              placeholder="Select priority"
              options={priorityOptions}
              value={formData?.priority || 'normal'}
              onChange={(value) => handleChange('priority', value)}
            />

            <Select
              label="Status"
              placeholder="Select status"
              options={statusOptions}
              value={formData?.status || 'todo'}
              onChange={(value) => handleChange('status', value)}
            />
          </div>

          <Input
            type="date"
            label="Due Date"
            description="When should this task be completed?"
            value={formData?.due_date || ''}
            onChange={(e) => handleChange('due_date', e?.target?.value)}
          />
        </div>

        <div className="flex justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={creating}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={creating}
            className="flex items-center gap-2"
          >
            {creating ? (
              <>
                <Icon name="Loader2" size={18} className="animate-spin" />
                Creating...
              </>
            ) : (
              <>
                <Icon name="Check" size={18} />
                Create Task
              </>
            )}
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default CreateTaskModal;